import { useEffect } from "react"
import { ApiError } from "../api/client"
import { useT } from "../lib/i18n"

interface ErrorModalProps {
  /** The error to show. When null the modal is hidden. */
  error: unknown
  /** Optional headline describing what failed (defaults to a generic title). */
  title?: string
  onClose: () => void
  /** When provided, shows a Retry button that closes the modal first. */
  onRetry?: () => void
}

/** Centered, dismissable modal that surfaces an error clearly — including the
 *  HTTP status and the backend-provided detail when the error is an ApiError. */
export function ErrorModal({ error, title, onClose, onRetry }: ErrorModalProps) {
  const { t } = useT()

  useEffect(() => {
    if (!error) return
    const onKey = (e: KeyboardEvent) => { if (e.key === "Escape") onClose() }
    window.addEventListener("keydown", onKey)
    return () => window.removeEventListener("keydown", onKey)
  }, [error, onClose])

  if (!error) return null

  const status = error instanceof ApiError ? error.status : null
  const detail = error instanceof ApiError
    ? (error.detail ?? error.message)
    : error instanceof Error ? error.message : String(error)

  return (
    <div
      role="alertdialog"
      aria-modal="true"
      onClick={onClose}
      style={{
        position: "fixed", inset: 0, zIndex: 1000,
        background: "rgba(0,0,0,0.55)",
        display: "grid", placeItems: "center", padding: 16,
      }}
    >
      <div
        onClick={(e) => e.stopPropagation()}
        style={{
          width: "min(520px, 100%)",
          background: "var(--surface)",
          color: "var(--text)",
          border: "1px solid var(--border)",
          borderRadius: "var(--radius)",
          boxShadow: "0 24px 60px rgba(0,0,0,0.5)",
          overflow: "hidden",
        }}
      >
        <div style={{
          display: "flex", alignItems: "center", gap: 10,
          padding: "16px 20px", borderBottom: "1px solid var(--border)",
        }}>
          <span aria-hidden style={{
            width: 28, height: 28, flex: "0 0 auto", borderRadius: "50%",
            background: "#ff7a85", color: "#1a1a1a", display: "grid",
            placeItems: "center", fontWeight: 700, fontSize: 16,
          }}>!</span>
          <strong style={{ fontSize: 16 }}>{title ?? t("error_title")}</strong>
        </div>

        <div style={{ padding: "16px 20px", display: "grid", gap: 10 }}>
          {status !== null && (
            <div style={{ fontSize: 13, color: "var(--muted)" }}>
              {t("error_status")}: <code style={{ color: "#ff7a85" }}>HTTP {status}</code>
            </div>
          )}
          <div>
            <div style={{ fontSize: 13, color: "var(--muted)", marginBottom: 4 }}>
              {t("error_detail")}
            </div>
            <pre style={{
              margin: 0, padding: "10px 12px",
              background: "var(--bg, rgba(0,0,0,0.25))",
              border: "1px solid var(--border)", borderRadius: 8,
              fontSize: 12.5, lineHeight: 1.5, whiteSpace: "pre-wrap",
              wordBreak: "break-word", maxHeight: 220, overflow: "auto",
            }}>{detail}</pre>
          </div>
        </div>

        <div style={{
          display: "flex", justifyContent: "flex-end", gap: 8,
          padding: "12px 20px", borderTop: "1px solid var(--border)",
        }}>
          {onRetry && (
            <button type="button" onClick={() => { onClose(); onRetry() }} style={btnGhost}>
              {t("error_retry")}
            </button>
          )}
          <button type="button" onClick={onClose} style={btnPrimary} autoFocus>
            {t("error_close")}
          </button>
        </div>
      </div>
    </div>
  )
}

const btnBase: React.CSSProperties = {
  padding: "8px 16px", borderRadius: 8, fontSize: 14,
  cursor: "pointer", border: "1px solid var(--border)",
}
const btnGhost: React.CSSProperties = {
  ...btnBase, background: "transparent", color: "var(--text)",
}
const btnPrimary: React.CSSProperties = {
  ...btnBase, background: "var(--accent)", color: "var(--accent-fg)",
  border: "1px solid var(--accent)",
}
